/**
 * IDs ordenáveis por tempo, estilo snowflake.
 *
 * Porquê não UUID v4: a paginação do chat é `WHERE id < cursor ORDER BY id DESC`.
 * Com IDs aleatórios isso não significa nada e ficas obrigado a indexar
 * `createdAt` e a lidar com empates ao milissegundo. Com snowflake, o índice
 * primário JÁ é a ordem cronológica — uma varredura, zero ordenação.
 *
 * Layout (64 bits, devolvido como string decimal):
 *   41 bits  ms desde EPOCH   (~69 anos)
 *   10 bits  id do worker     (0-1023)
 *   12 bits  sequência        (4096 ids por ms por worker)
 */
/** 2026-01-01T00:00:00Z. */
export const EPOCH = 1767225600000n;
const WORKER_BITS = 10n;
const SEQUENCE_BITS = 12n;
const MAX_WORKER = (1 << Number(WORKER_BITS)) - 1;
const MAX_SEQUENCE = (1n << SEQUENCE_BITS) - 1n;
const WORKER_SHIFT = SEQUENCE_BITS;
const TIMESTAMP_SHIFT = SEQUENCE_BITS + WORKER_BITS;
export class SnowflakeGenerator {
    #workerId;
    #lastTimestamp = -1n;
    #sequence = 0n;
    constructor(workerId = 0) {
        if (!Number.isInteger(workerId) || workerId < 0 || workerId > MAX_WORKER) {
            throw new RangeError(`workerId tem de estar entre 0 e ${MAX_WORKER}`);
        }
        this.#workerId = BigInt(workerId);
    }
    next() {
        let now = BigInt(Date.now());
        // Relógio andou para trás (NTP). Esperar activamente é feio mas é a
        // única saída correcta: emitir IDs duplicados seria pior.
        if (now < this.#lastTimestamp) {
            const waitUntil = this.#lastTimestamp;
            while (now < waitUntil)
                now = BigInt(Date.now());
        }
        if (now === this.#lastTimestamp) {
            this.#sequence = (this.#sequence + 1n) & MAX_SEQUENCE;
            if (this.#sequence === 0n) {
                // Esgotámos os 4096 deste ms: esperar pelo próximo.
                while (BigInt(Date.now()) <= this.#lastTimestamp) {
                    /* spin */
                }
                now = BigInt(Date.now());
            }
        }
        else {
            this.#sequence = 0n;
        }
        this.#lastTimestamp = now;
        const id = ((now - EPOCH) << TIMESTAMP_SHIFT) | (this.#workerId << WORKER_SHIFT) | this.#sequence;
        return id.toString(10);
    }
}
/**
 * Gerador do processo. O worker id vem do env em deploys multi-instância
 * (WORKER_ID=0..1023) — dois processos com o mesmo id colidem.
 */
export const snowflake = new SnowflakeGenerator(Number(globalThis.process?.env?.WORKER_ID ?? 0));
export function generateId() {
    return snowflake.next();
}
/** Timestamp embebido no ID. Zero acessos à base de dados. */
export function timestampOf(id) {
    const ms = (BigInt(id) >> TIMESTAMP_SHIFT) + EPOCH;
    return new Date(Number(ms));
}
/** Cursor sintético para "tudo antes desta data" sem coluna extra. */
export function idFromTimestamp(date) {
    const ms = BigInt(typeof date === "number" ? date : date.getTime());
    return (((ms - EPOCH) << TIMESTAMP_SHIFT) | 0n).toString(10);
}
export function isSnowflake(value) {
    if (typeof value !== "string" || value.length === 0 || value.length > 20)
        return false;
    if (!/^\d+$/.test(value))
        return false;
    try {
        const n = BigInt(value);
        return n >= 0n && n < 1n << 63n;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=snowflake.js.map